import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark';

export type ThemeSettings = {
  mode: ThemeMode;
  primary: string; // hex color, e.g. #1A1A1A
};

type ThemeContextValue = {
  theme: ThemeSettings;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  setPrimary: (hex: string) => void;
  reset: () => void;
};

const STORAGE_KEY = 'agd.theme';
const DEFAULT_THEME: ThemeSettings = { mode: 'light', primary: '#1A1A1A' };

const Ctx = createContext<ThemeContextValue | undefined>(undefined);

function isHexColor(v: string) {
  return /^#[0-9a-fA-F]{6}$/.test(v);
}

function loadTheme(): ThemeSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THEME;
    const parsed = JSON.parse(raw) as Partial<ThemeSettings>;
    const mode: ThemeMode = parsed.mode === 'dark' ? 'dark' : 'light';
    const primary = typeof parsed.primary === 'string' && isHexColor(parsed.primary) ? parsed.primary : DEFAULT_THEME.primary;
    return { mode, primary };
  } catch {
    return DEFAULT_THEME;
  }
}

function applyToDom(theme: ThemeSettings) {
  document.documentElement.setAttribute('data-theme', theme.mode);
  document.documentElement.style.setProperty('--primary', theme.primary);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeSettings>(() => loadTheme());

  useEffect(() => {
    applyToDom(theme);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
    } catch {
      // ignore write errors (private mode, disabled storage)
    }
  }, [theme]);

  const setMode = useCallback((mode: ThemeMode) => {
    setTheme((t) => ({ ...t, mode }));
  }, []);

  const toggleMode = useCallback(() => {
    setTheme((t) => ({ ...t, mode: t.mode === 'dark' ? 'light' : 'dark' }));
  }, []);

  const setPrimary = useCallback((hex: string) => {
    const v = hex.trim();
    if (!isHexColor(v)) return;
    setTheme((t) => ({ ...t, primary: v.toUpperCase() }));
  }, []);

  const reset = useCallback(() => setTheme(DEFAULT_THEME), []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setMode, toggleMode, setPrimary, reset }),
    [theme, setMode, toggleMode, setPrimary, reset],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme must be used within ThemeProvider');
  return v;
}

